import type {
  AllocationLine,
  AllocationResult,
  BatchRecord,
  DecisionPlan,
  ExpiryClass,
  WarehouseDataset,
} from "../types";
import {
  batchExpirySnapshot,
  expiryClassLabel,
  expiryUrgency,
  shanghaiDateString,
} from "./batch";

export type CustomerPlanId = "quarter" | "third" | "half";

export interface CustomerPlanDefinition {
  id: CustomerPlanId;
  symbol: string;
  shortLabel: string;
  customer: string;
  acceptance: string;
  demandFactor: number;
  minimumClass: ExpiryClass;
  eligibleClasses: ExpiryClass[];
}

export const CUSTOMER_PLANS: CustomerPlanDefinition[] = [
  {
    id: "quarter",
    symbol: "¼",
    shortLabel: "临期快销",
    customer: "短周转零售客户",
    acceptance: "可接受 ¼ 临期至新鲜期",
    demandFactor: 0.65,
    minimumClass: "quarter",
    eligibleClasses: ["quarter", "third", "half", "fresh"],
  },
  {
    id: "third",
    symbol: "⅓",
    shortLabel: "常规渠道",
    customer: "常规渠道客户",
    acceptance: "要求至少 ⅓ 保质期剩余",
    demandFactor: 0.85,
    minimumClass: "third",
    eligibleClasses: ["third", "half", "fresh"],
  },
  {
    id: "half",
    symbol: "½",
    shortLabel: "长周期保障",
    customer: "长周期高要求客户",
    acceptance: "要求至少 ½ 保质期剩余",
    demandFactor: 1,
    minimumClass: "half",
    eligibleClasses: ["half", "fresh"],
  },
];

export interface ProductBatchView {
  batch: BatchRecord;
  expiryClass: ExpiryClass;
  expiresOn: string;
  producedOn: string;
  shelfLifeDays: number | null;
  remainingRatio: number | null;
  automatic: boolean;
  exclusionReason: string;
}

export interface ProductPlanAllocation extends AllocationResult {
  plan: CustomerPlanDefinition;
  demand: number;
  backupAllocated: number;
}

export interface ProductAllocation {
  sku: string;
  product: string;
  location: string;
  stock: number;
  batches: ProductBatchView[];
  plans: ProductPlanAllocation[];
}

export interface AllocationWorkbook {
  today: string;
  products: ProductAllocation[];
  plans: CustomerPlanDefinition[];
  excluded: {
    expired: number;
    recalled: number;
    unknown: number;
  };
}

function automaticInventory(batch: ProductBatchView) {
  return batch.automatic && batch.batch.quantity > 0;
}

function allocatePlan(
  batches: ProductBatchView[],
  ledger: Map<string, number>,
  plan: CustomerPlanDefinition,
  demand: number,
): ProductPlanAllocation {
  let left = demand;
  const lines: AllocationLine[] = [];
  const eligible = batches
    .filter((item) => automaticInventory(item) && plan.eligibleClasses.includes(item.expiryClass))
    .sort(
      (a, b) =>
        expiryUrgency(a.expiryClass) - expiryUrgency(b.expiryClass)
        || a.expiresOn.localeCompare(b.expiresOn)
        || a.batch.lot.localeCompare(b.batch.lot),
    );

  eligible.forEach((item) => {
    if (left <= 0) return;
    const available = ledger.get(item.batch.id) ?? 0;
    if (available <= 0) return;
    const allocated = Math.min(left, available);
    ledger.set(item.batch.id, available - allocated);
    left -= allocated;
    lines.push({
      batchId: item.batch.id,
      lot: item.batch.lot,
      expiresOn: item.expiresOn,
      expiryClass: item.expiryClass,
      allocated,
      location: item.batch.location,
      recallFlag: item.batch.recallFlag,
    });
  });

  const warnings = left > 0
    ? [`${plan.symbol} 方案满足约束后仍缺 ${left} 件，需补货或人工审批。`]
    : [];
  return {
    plan,
    demand,
    requested: demand,
    fulfilled: demand - left,
    shortfall: left,
    lines,
    warnings,
    backupAllocated: lines
      .filter((line) => line.expiryClass === "fresh")
      .reduce((sum, line) => sum + line.allocated, 0),
  };
}

export function buildAllocationWorkbook(
  dataset: WarehouseDataset,
  decisionPlan: DecisionPlan,
  today = shanghaiDateString(),
): AllocationWorkbook {
  const grouped = new Map<string, BatchRecord[]>();
  dataset.batches.forEach((batch) => {
    const rows = grouped.get(batch.sku) ?? [];
    rows.push(batch);
    grouped.set(batch.sku, rows);
  });

  const ledger = new Map<string, number>();
  const excluded = { expired: 0, recalled: 0, unknown: 0 };
  const products: ProductAllocation[] = [...grouped.entries()]
    .map(([sku, rows]) => {
      const batches: ProductBatchView[] = rows.map((batch) => {
        const snapshot = batchExpirySnapshot(batch, today);
        const exclusionReason = batch.recallFlag
          ? "召回批次"
          : snapshot.expiryClass === "expired"
            ? "已过期"
            : snapshot.expiryClass === "unknown" || snapshot.expiryClass === "none"
              ? "效期信息待核对"
              : "";
        const view: ProductBatchView = {
          batch,
          expiryClass: snapshot.expiryClass,
          expiresOn: snapshot.expiresOn,
          producedOn: snapshot.producedOn,
          shelfLifeDays: snapshot.shelfLifeDays,
          remainingRatio: snapshot.remainingRatio,
          automatic: !exclusionReason,
          exclusionReason,
        };
        if (exclusionReason === "召回批次") excluded.recalled += batch.quantity;
        else if (exclusionReason === "已过期") excluded.expired += batch.quantity;
        else if (exclusionReason === "效期信息待核对") excluded.unknown += batch.quantity;
        ledger.set(batch.id, exclusionReason ? 0 : Math.max(0, batch.quantity));
        return view;
      });
      batches.sort(
        (a, b) =>
          expiryUrgency(a.expiryClass) - expiryUrgency(b.expiryClass)
          || a.expiresOn.localeCompare(b.expiresOn),
      );
      const first = batches[0];
      const analysis = decisionPlan.products.find((item) => item.sku === sku);
      const demandBase = Math.max(
        20,
        Math.ceil(analysis?.suggestedQuantity ?? first?.batch.quantity ?? 20),
      );
      const productPlans = CUSTOMER_PLANS.map((plan) =>
        allocatePlan(
          batches,
          ledger,
          plan,
          Math.max(1, Math.ceil(demandBase * plan.demandFactor)),
        ),
      );
      return {
        sku,
        product: analysis?.product ?? first?.batch.product ?? sku,
        location: analysis?.location ?? first?.batch.location ?? "未分配",
        stock: batches.reduce((sum, item) => sum + Math.max(0, item.batch.quantity), 0),
        batches,
        plans: productPlans,
      };
    })
    .sort((a, b) => b.stock - a.stock || a.sku.localeCompare(b.sku));

  return {
    today,
    products,
    plans: CUSTOMER_PLANS,
    excluded,
  };
}

export function allocationExportRows(workbook: AllocationWorkbook) {
  return workbook.products.flatMap((product) =>
    product.plans.map((result) => ({
      sku: product.sku,
      product: product.product,
      today: workbook.today,
      plan: `${result.plan.symbol} ${result.plan.shortLabel}`,
      customer: result.plan.customer,
      acceptance: result.plan.acceptance,
      demand: result.demand,
      fulfilled: result.fulfilled,
      shortfall: result.shortfall,
      lots: result.lines.map((line) => line.lot).join(" / "),
      batches: result.lines.map((line) => line.batchId).join(" / "),
      classes: result.lines.map((line) => expiryClassLabel(line.expiryClass)).join(" / "),
      quantities: result.lines.map((line) => line.allocated).join(" / "),
      locations: result.lines.map((line) => line.location).join(" / "),
      backup: result.backupAllocated,
      warning: result.warnings.join(" "),
    })),
  );
}
